// <cha-maker-form> — Cha Maker PDP: Set × Color variants, gallery, add to cart, sticky bar, info drawers,
// STOQ "Notify me" for sold-out colours (docs/10 FC-9).
const money = (cents, fmt) => {
  const whole = cents % 100 === 0;
  const n = whole ? String(Math.round(cents / 100)) : (cents / 100).toFixed(2);
  const withSep = n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (fmt || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, withSep);
};

class ChaMakerForm extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.fmt = document.documentElement.dataset.moneyFormat;
    const qv = +new URL(location.href).searchParams.get('variant');
    const cur = this.variants.find((v) => v.id === qv) || this.variants.find((v) => v.id === +this.dataset.current) || this.variants[0];
    this.mode = this.dataset.mode;
    this.state = { set: cur.set, color: cur.color, img: 0 };
    this.fromUrl = this.dataset.mode === 'bundles' && cur.id === qv;
    this.input = this.querySelector('[data-variant-input]');
    this.form = this.querySelector('[data-product-form]');

    // options
    this.addEventListener('click', (e) => {
      const sw = e.target.closest('.zp-swb'); if (sw) { this.pick({ color: sw.dataset.color }); return; }
      const sh = e.target.closest('.zs-h'); if (sh) { this.pick({ set: sh.closest('.zs').dataset.set }); return; }
      const th = e.target.closest('.zp-th'); if (th) { this.go(+th.dataset.go); return; }
      const st = e.target.closest('.zp-gbtn'); if (st) { this.go(this.state.img + +st.dataset.step); return; }
      const dOpen = e.target.closest('[data-drawer-open]'); if (dOpen) { this.drawer(dOpen.dataset.drawerOpen, true, dOpen); return; }
      if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-drawer-veil]')) { this.drawer(this.openDrawer, false); return; }
      if (e.target.closest('[data-notify]')) { this.notify(); return; }
      if (e.target.closest('[data-bar-add]')) { this.add(); }
    });
    this.querySelectorAll('[role="radiogroup"]').forEach((g) => g.addEventListener('keydown', (e) => this.radioKeys(e, g)));
    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.openDrawer) this.drawer(this.openDrawer, false); });

    // phone gallery: keep the counter/thumb state in sync with the swipe strip
    this.gal = this.querySelector('.zp-gal');
    this.gal?.addEventListener('scroll', () => {
      if (getComputedStyle(this.gal).overflowX !== 'auto') return;
      const items = [...this.gal.querySelectorAll('.zp-gi')];
      const x = this.gal.scrollLeft; let i = 0;
      items.forEach((it, k) => { if (it.offsetLeft - items[0].offsetLeft <= x + it.offsetWidth * 0.4) i = k; });
      this.state.img = i;
    }, { passive: true });

    // sticky bar once the buy button has scrolled past
    const cta = this.querySelector('.zp-buyrow');
    this.bar = this.querySelector('[data-bar]');
    if (cta && this.bar && 'IntersectionObserver' in window) {
      this.io = new IntersectionObserver(([en]) => {
        const on = !en.isIntersecting && en.boundingClientRect.top < 0;
        this.bar.classList.toggle('on', on);
        this.bar.setAttribute('aria-hidden', String(!on));
        this.bar.querySelector('[data-bar-add]').tabIndex = on ? 0 : -1;
        document.documentElement.classList.toggle('bar-on', on);
      });
      this.io.observe(cta);
    }
    if (this.fromUrl) this.pick({}); // bundles: ?variant= is a bundle variant id the server could not resolve
  }
  disconnectedCallback() { this.io?.disconnect(); document.documentElement.classList.remove('bar-on'); }

  get variant() { return this.variants.find((v) => v.set === this.state.set && v.color === this.state.color); }

  pick(next) {
    Object.assign(this.state, next);
    const v = this.variant; if (!v) return;
    this.input.value = v.id;
    const price = money(v.price, this.fmt);
    this.querySelectorAll('[data-price]').forEach((el) => { el.textContent = price; });
    const cmp = this.querySelector('[data-compare]'); const hasCmp = v.compare > v.price;
    if (cmp) { cmp.hidden = !hasCmp; cmp.querySelector('[data-compare-v]').textContent = money(v.compare, this.fmt); }
    const save = this.querySelector('[data-save]');
    if (save) { save.hidden = !hasCmp; save.textContent = save.dataset.tpl.replace('[amount]', money(v.compare - v.price, this.fmt)); }
    const stock = this.querySelector('[data-stock]');
    if (stock) { stock.querySelector('span').textContent = v.available ? stock.dataset.in : stock.dataset.out; stock.classList.toggle('out', !v.available); }
    this.querySelectorAll('[data-color-name]').forEach((el) => { el.textContent = v.color; });
    this.querySelectorAll('[data-ship-note]').forEach((el) => { el.textContent = v.ship; });
    const line = this.querySelector('[data-ship-line]');
    if (line) line.textContent = v.ship + (line.dataset.suffix ? ' · ' + line.dataset.suffix : '');
    const barSet = this.querySelector('[data-bar-set]'); if (barSet) barSet.textContent = v.set.replace(/^Cha Maker /, '').replace(/^Cha /, '');

    this.querySelectorAll('.zp-swb').forEach((b) => { const on = b.dataset.color === v.color; b.classList.toggle('on', on); b.setAttribute('aria-checked', String(on)); b.tabIndex = on ? 0 : -1; });
    this.querySelectorAll('.zs').forEach((c) => {
      const on = c.dataset.set === v.set; c.classList.toggle('on', on);
      const h = c.querySelector('.zs-h'); h.setAttribute('aria-checked', String(on)); h.tabIndex = on ? 0 : -1;
      c.querySelectorAll('.zs-in').forEach((r) => { r.hidden = r.dataset.color !== v.color; });
    });
    const sn = this.querySelector('[data-set-note]'); if (sn) sn.hidden = !!sn.dataset.hideSet && sn.dataset.hideSet === v.set;
    const add = this.querySelector('[data-add]'); const notify = this.querySelector('[data-notify]');
    add.hidden = !v.available; notify.hidden = v.available;
    this.label(add, add.dataset.label.replace('[price]', price));
    this.bar?.querySelector('[data-bar-add]')?.toggleAttribute('disabled', !v.available);
    this.error('');

    const url = new URL(location.href); url.searchParams.set('variant', v.id);
    history.replaceState(history.state, '', url);
  }
  label(btn, text) { const s = btn.querySelector('.roll > span'); if (s) { s.textContent = text; s.dataset.t = text; } }
  radioKeys(e, g) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(e.key in keys)) return;
    e.preventDefault();
    const items = [...g.querySelectorAll('[role="radio"]')];
    const i = items.indexOf(document.activeElement);
    const nx = items[(i + keys[e.key] + items.length) % items.length];
    nx.focus(); nx.click();
  }
  go(i) {
    const items = [...this.querySelectorAll('.zp-gi')]; if (!items.length) return;
    i = (i + items.length) % items.length; this.state.img = i;
    if (getComputedStyle(this.gal).overflowX === 'auto') { items[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' }); return; }
    items.forEach((it, k) => { it.classList.toggle('on', k === i); it.setAttribute('aria-hidden', String(k !== i)); });
    this.querySelectorAll('.zp-th').forEach((t, k) => { t.classList.toggle('on', k === i); t.toggleAttribute('aria-current', k === i); });
    const c = this.querySelector('[data-gcur]'); if (c) c.textContent = String(i + 1).padStart(2, '0');
  }
  error(msg) { const el = this.querySelector('[data-error]'); if (el) { el.textContent = msg; el.hidden = !msg; } }
  async add() {
    const v = this.variant; if (!v || !v.available) return;
    const btn = this.querySelector('[data-add]');
    btn.setAttribute('aria-busy', 'true');
    try {
      if (!window.ClCart) { this.form.submit(); return; }
      await window.ClCart.add([{ id: v.id, quantity: 1 }]);
      this.label(btn, btn.dataset.added);
      clearTimeout(this.t); this.t = setTimeout(() => this.label(btn, btn.dataset.label.replace('[price]', money(this.variant.price, this.fmt))), 2400);
    } catch (err) { this.error(err.message); }
    finally { btn.removeAttribute('aria-busy'); }
  }
  async notify() {
    const v = this.variant; const rr = window._RestockRocket; const cfg = window._RestockRocketConfig;
    if (!rr?.openModal) { this.notifyFallback(); return; }
    // bundles: subscribe to the Cha Maker unit variant that actually restocks, not the page product
    if (this.mode === 'bundles' && v.nid) {
      try {
        const unit = await fetch(`/products/${this.dataset.unitHandle}.js`).then((r) => r.json());
        rr.openModal(unit, v.nid); return;
      } catch (_) { /* fall through to the page product */ }
    }
    if (cfg?.product) rr.openModal(cfg.product, v.pv || v.id); // STOQ preselects the variant from its id (an object is ignored)
    else this.notifyFallback();
  }
  notifyFallback() { this.error(this.querySelector('[data-notify]').dataset.fallback || window.ClStrings?.notify_unavailable || 'Notifications are unavailable right now. Please email cs@elaiksir.com.'); }
  drawer(name, open, trigger) {
    if (!name) return;
    const d = this.querySelector(`[data-drawer="${name}"]`); const veil = this.querySelector(`[data-drawer-veil="${name}"]`);
    if (!d) return;
    d.classList.toggle('open', open); veil?.classList.toggle('open', open); d.setAttribute('aria-hidden', String(!open));
    document.documentElement.classList.toggle('lock', open);
    if (open) { this.openDrawer = name; this.prev = trigger; setTimeout(() => d.querySelector('.sq')?.focus({ preventScroll: true }), 50); }
    else { this.openDrawer = null; this.prev?.focus({ preventScroll: true }); }
  }
}
if (!customElements.get('cha-maker-form')) customElements.define('cha-maker-form', ChaMakerForm);
